import {
  AddPortfolioReviewLinePayload,
  CreatePortfolioReviewPayload,
  ExternalHoldings,
  PaginatedResponse,
  PoorlyPerformingFund,
  PortfolioExposure,
  PortfolioJourney,
  PortfolioJourneyParams,
  PortfolioReview,
  PortfolioReviewAccountParams,
  PortfolioReviewExposure,
  PortfolioReviewInsights,
  PortfolioReviewLineMutationResponse,
  PortfolioReviewListItem,
  PortfolioReviewListParams,
  PortfolioReviewOverview,
  PortfolioReviewOverviewParams,
  PortfolioReviewReport,
  ReviewBenchmark,
  SwitchCandidate,
  UpdatePortfolioReviewLinePayload,
} from "../types";
import { api } from "./axios";

// Landing page diagnostics, derived live from the account's holdings.

export function getPortfolioReviewOverview({
  include,
  ...params
}: PortfolioReviewOverviewParams): Promise<PortfolioReviewOverview> {
  return api.get("portfolio-review/overview/", {
    params: { ...params, include: include?.join(",") || undefined },
  });
}

export function getPoorlyPerformingFunds(
  params: PortfolioReviewAccountParams
): Promise<PoorlyPerformingFund[]> {
  return api.get("portfolio-review/poorly-performing/", { params });
}

export function getPortfolioExposure(
  params: PortfolioReviewAccountParams
): Promise<PortfolioExposure> {
  return api.get("portfolio-review/exposure/", { params });
}

export function getExternalHoldings(
  params: Pick<PortfolioReviewAccountParams, "investment_account_guid">
): Promise<ExternalHoldings> {
  return api.get("portfolio-review/external-holdings/", { params });
}

export function getPortfolioJourney(
  params: PortfolioJourneyParams
): Promise<PortfolioJourney> {
  return api.get("portfolio-review/journey/", { params });
}

export function getReviewBenchmarks(): Promise<ReviewBenchmark[]> {
  return api.get("portfolio-review/benchmarks/");
}

// Reviews

export function getPortfolioReviews(
  params: PortfolioReviewListParams
): Promise<PaginatedResponse<PortfolioReviewListItem>> {
  return api.get("portfolio-reviews/", { params });
}

export function createPortfolioReview(
  payload: CreatePortfolioReviewPayload
): Promise<PortfolioReview> {
  return api.post("portfolio-reviews/", payload);
}

export function getPortfolioReview(id: number): Promise<PortfolioReview> {
  return api.get(`portfolio-reviews/${id}/`);
}

export function deletePortfolioReview(id: number): Promise<void> {
  return api.delete(`portfolio-reviews/${id}/`);
}

export function finalizePortfolioReview(id: number): Promise<PortfolioReview> {
  return api.post(`portfolio-reviews/${id}/finalize/`, {});
}

export function getPortfolioReviewInsights(
  id: number
): Promise<PortfolioReviewInsights> {
  return api.get(`portfolio-reviews/${id}/insights/`);
}

// The landing overview frozen as it stood when the review was created.
export function getPortfolioReviewFrozenOverview(
  id: number
): Promise<PortfolioReviewOverview> {
  return api.get(`portfolio-reviews/${id}/overview/`);
}

export function getPortfolioReviewExposure(
  id: number
): Promise<PortfolioReviewExposure> {
  return api.get(`portfolio-reviews/${id}/exposure/?limit=10`);
}

export function getPortfolioReviewReport(
  id: number
): Promise<PortfolioReviewReport> {
  return api.get(`portfolio-reviews/${id}/report/`);
}

// Review lines

export function addPortfolioReviewLine({
  reviewId,
  ...payload
}: AddPortfolioReviewLinePayload & {
  reviewId: number;
}): Promise<PortfolioReviewLineMutationResponse> {
  return api.post(`portfolio-reviews/${reviewId}/lines/`, payload);
}

export function updatePortfolioReviewLines({
  reviewId,
  lines,
}: {
  reviewId: number;
  lines: (UpdatePortfolioReviewLinePayload & { id: number })[];
}): Promise<PortfolioReviewLineMutationResponse> {
  return api.patch(`portfolio-reviews/${reviewId}/lines/`, { lines });
}

export function updatePortfolioReviewLine({
  reviewId,
  lineId,
  ...payload
}: UpdatePortfolioReviewLinePayload & {
  reviewId: number;
  lineId: number;
}): Promise<PortfolioReviewLineMutationResponse> {
  return api.patch(`portfolio-reviews/${reviewId}/lines/${lineId}/`, payload);
}

export function deletePortfolioReviewLine({
  reviewId,
  lineId,
}: {
  reviewId: number;
  lineId: number;
}): Promise<PortfolioReviewLineMutationResponse> {
  return api.delete(`portfolio-reviews/${reviewId}/lines/${lineId}/`);
}

export function getSwitchCandidates({
  reviewId,
  lineId,
  q,
  page,
}: {
  reviewId: number;
  lineId: number;
  q?: string;
  page?: number;
}): Promise<PaginatedResponse<SwitchCandidate>> {
  return api.get(
    `portfolio-reviews/${reviewId}/lines/${lineId}/switch-candidates/`,
    { params: { q: q || undefined, page } }
  );
}
